import { useRef, useState } from "react";
import { Camera, ImagePlus, Video, X, Loader2, AlertCircle } from "lucide-react";
import { uploadAsset } from "@/api/evidences";

export default function MediaCapture({ media = [], onChange }) {
  const cameraInput = useRef(null);
  const galleryInput = useRef(null);
  const videoInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFiles = async (files, kind) => {
    if (!files || !files.length) return;
    setError(null);
    setUploading(true);

    const next = [...media];
    for (const file of Array.from(files)) {
      const tempId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      try {
        const result = await uploadAsset(file);
        next.push({
          id: tempId,
          type: result.type || (kind === "video" ? "video" : "image"),
          name: result.fileName || file.name,
          mimeType: result.mimeType || file.type || null,
          size: result.size || file.size,
          url: result.url,
        });
      } catch (err) {
        console.error("upload error", err);
        const message =
          err?.response?.data?.message ||
          (err?.response?.status === 413 ? "Fichero demasiado grande" : "Error subiendo el archivo");
        setError(message);
      }
    }

    onChange?.(next);
    setUploading(false);
  };

  const remove = (key) => onChange?.(media.filter((m) => (m.id || m.url) !== key));

  const trigger = (ref) => ref.current?.click();

  const btnBase =
    "flex flex-col items-center justify-center h-20 rounded-sm bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 hover:border-orange-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors gap-1 disabled:opacity-50 disabled:cursor-not-allowed";
  const labelBase =
    "font-mono-tech text-[9px] uppercase tracking-widest text-zinc-700 dark:text-zinc-300";

  return (
    <div className="space-y-3" data-testid="media-capture">
      <div className="grid grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => trigger(cameraInput)}
          disabled={uploading}
          className={btnBase}
          data-testid="media-camera-btn"
        >
          <Camera className="w-5 h-5 text-orange-500" />
          <span className={labelBase}>Cámara</span>
        </button>
        <button
          type="button"
          onClick={() => trigger(galleryInput)}
          disabled={uploading}
          className={btnBase}
          data-testid="media-gallery-btn"
        >
          <ImagePlus className="w-5 h-5 text-orange-500" />
          <span className={labelBase}>Galería</span>
        </button>
        <button
          type="button"
          onClick={() => trigger(videoInput)}
          disabled={uploading}
          className={btnBase}
          data-testid="media-video-btn"
        >
          <Video className="w-5 h-5 text-orange-500" />
          <span className={labelBase}>Vídeo</span>
        </button>
      </div>

      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files, "image")}
        data-testid="media-camera-input"
      />
      <input
        ref={galleryInput}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files, "image")}
        data-testid="media-gallery-input"
      />
      <input
        ref={videoInput}
        type="file"
        accept="video/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files, "video")}
        data-testid="media-video-input"
      />

      {uploading && (
        <div className="flex items-center gap-2 text-orange-500 font-mono-tech text-[10px] uppercase tracking-widest">
          <Loader2 className="w-3 h-3 animate-spin" />
          Subiendo a Cloudflare...
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-2 rounded-sm bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span className="font-mono-tech text-[10px]">{error}</span>
        </div>
      )}

      {media.length > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid="media-grid">
          {media.map((m) => (
            <div
              key={m.id || m.url}
              className="relative aspect-square rounded-sm overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 group"
              data-testid={`media-item-${m.type}`}
            >
              {m.type === "video" ? (
                <video src={m.url} className="w-full h-full object-cover" muted />
              ) : (
                <img src={m.url} alt={m.name} className="w-full h-full object-cover" />
              )}
              {m.type === "video" && (
                <div className="absolute top-1 left-1 bg-black/70 px-1.5 py-0.5 rounded-sm flex items-center gap-1">
                  <Video className="w-2.5 h-2.5 text-orange-500" />
                  <span className="font-mono-tech text-[8px] text-white uppercase">VID</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => remove(m.id || m.url)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/80 text-white flex items-center justify-center hover:bg-red-500 transition-colors"
                data-testid="media-remove-btn"
                aria-label="Eliminar"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
